import {
	Button,
	Card,
	CardLayoutType,
	Colour,
	ColourStateSolid,
	FlexAlign,
	FlexGap,
	FlexJustify,
	ListItem,
	Pill,
	StackHorizontal,
	StackVertical,
	Tag,
	ToggleSwitch,
	ToggleType,
	Tooltip,
	useLayout,
} from '@ukpostbox/frontend-framework';
import { FormatCurrency, ScrollToActive } from '@ukpostbox/helpers';
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faXmarkCircle } from '@fortawesome/pro-solid-svg-icons/faXmarkCircle';
import { SignUpStepType } from '../Types/SignUp/SignUpStep';
import { SignUpPlanType } from '../Types/SignUp/SignUpPlan';
import AccountCreation from '../Components/AccountCreation';
import {
	BusinessAddressesOverview,
	LetterPerks,
	LettersOverview,
	ParcelPerks,
	ParcelsOverview,
	PlanPerks,
	PlansOverview,
	PoBoxOverview,
	StreetAddressesOverview,
} from '../../_Global/Data/SignUp';
import { SignUpPerkType } from '../Types/SignUp/SignUpPerk';
import SignupDevNav from '../Components/SignupDevNav';

const initialActiveStep = { id: -1, complete: false, visible: true, value: null };
const initialStep = { complete: false, visible: false, value: null };
const initialSummary = { complete: false, visible: false, value: null, showAnnualCost: true };

type SignUpTotalsType = {
	planId: number;
	poBoxAddressCost: number;
	letterOptionCost: number;
	streetAddressCost: number;
	businessAddressCost: number;
	parcelOptionCost: number;
};

function MonthlyTotal(props: SignUpTotalsType) {
	let total: string = '£0';

	switch (props.planId) {
		case 0:
			total = FormatCurrency(props.poBoxAddressCost + props.letterOptionCost, true);
			break;
		case 1:
			total = FormatCurrency(props.streetAddressCost + props.letterOptionCost, true);
			break;
		case 2:
			total = FormatCurrency(props.businessAddressCost + props.letterOptionCost, true);
			break;
		case 3:
			total = FormatCurrency(props.parcelOptionCost, true);
			break;
		case 4:
		default:
			total = FormatCurrency(props.letterOptionCost, true);
			break;
	}

	return <div className='pricing-block-price'>{total}</div>;
}

function AnnualTotal(props: SignUpTotalsType) {
	let total: string = '£0';

	switch (props.planId) {
		case 0:
			total = FormatCurrency(props.poBoxAddressCost * 10 + props.letterOptionCost * 10, true);
			break;
		case 1:
			total = FormatCurrency(props.streetAddressCost * 10 + props.letterOptionCost * 10, true);
			break;
		case 2:
			total = FormatCurrency(props.businessAddressCost * 10 + props.letterOptionCost * 10, true);
			break;
		case 3:
			total = FormatCurrency(props.parcelOptionCost * 10, true);
			break;
		case 4:
		default:
			total = FormatCurrency(props.letterOptionCost * 10, true);
			break;
	}

	return <div className='pricing-block-price'>{total}</div>;
}

function SignUpThree() {
	const { setContentOnlyLayout } = useLayout();

	const [plan, setPlan] = useState<SignUpStepType>(initialActiveStep);
	const [streetAddress, setStreetAddress] = useState<SignUpStepType>(initialStep);
	const [streetAddressDetails, setStreetAddressDetails] = useState<any>(initialStep);
	const [poBoxAddress, setPoBoxAddress] = useState<SignUpStepType>(initialStep);
	const [poBoxAddressDetails, setPoBoxAddressDetails] = useState<any>(initialStep);
	const [businessAddress, setBusinessAddress] = useState<SignUpStepType>(initialStep);
	const [businessAddressDetails, setBusinessAddressDetails] = useState<any>(initialStep);
	const [letterOption, setLetterOption] = useState<SignUpStepType>({ ...LettersOverview[0], ...initialStep });
	const [parcelOption, setParcelOption] = useState<SignUpStepType>({ ...ParcelsOverview[0], ...initialStep });
	const [summary, setSummary] = useState<SignUpStepType>(initialSummary);

	const [annual, setAnnual] = useState<boolean>(true);

	const [accountCreation, setAccountCreation] = useState<SignUpStepType>(initialStep);

	useEffect(() => {
		if (plan.complete) {
			ScrollToActive(
				document.querySelector('.sign-up-section:not(.sign-up-section-hide)') as Element,
				'sign-up-section',
			);
		}
	}, [plan, streetAddress, businessAddress, letterOption, parcelOption, summary, accountCreation]);

	useEffect(() => {
		setContentOnlyLayout(true);
	}, []);

	const UpdatePlan = (editing: boolean, e?: SignUpPlanType) => {
		setPlan({ ...e, complete: true, visible: editing, value: e || (editing ? plan.value : null) });
	};

	const UpdatePoBoxAddress = (editing: boolean, e?: SignUpPlanType) => {
		setPoBoxAddress({
			...e,
			complete: editing ? poBoxAddress.complete : true,
			visible: editing,
			value: editing ? poBoxAddress.value : e || (editing ? poBoxAddress.value : null),
		});
	};

	const UpdateStreetAddress = (editing: boolean, e?: SignUpPlanType) => {
		setStreetAddress({
			...e,
			complete: editing ? streetAddress.complete : true,
			visible: editing,
			value: editing ? streetAddress.value : e || (editing ? streetAddress.value : null),
		});
	};

	const UpdateBusinessAddress = (editing: boolean, e?: SignUpPlanType) => {
		setBusinessAddress({
			...e,
			complete: editing ? businessAddress.complete : true,
			visible: editing,
			value: editing ? businessAddress.value : e || (editing ? businessAddress.value : null),
		});
	};

	const UpdateLetterOption = (editing: boolean, e: string) => {
		const letter = LettersOverview[parseInt(e, 10)];
		setLetterOption({
			...letter,
			complete: editing ? letterOption.complete : true,
			visible: editing,
			value: editing ? letterOption.value : letter || (editing ? letterOption.value : null),
		});
	};

	const UpdateParcelOption = (editing: boolean, e: string) => {
		const parcel = ParcelsOverview[parseInt(e, 10)];
		setParcelOption({
			...parcel,
			complete: editing ? parcelOption.complete : true,
			visible: editing,
			value: editing ? parcelOption.value : parcel || (editing ? parcelOption.value : null),
		});
	};

	const UpdateAccountCreation = () => {
		setAccountCreation({ complete: accountCreation.complete, visible: true, value: accountCreation.value });
	};

	useEffect(() => {
		console.log(streetAddressDetails);
	}, [streetAddressDetails]);

	return (
		<div className='content-main'>
			<div className='container-narrow p-md'>
				<div className='flex-row align-center justify-center'>
					<div className='header-logo-container logo-big'>
						<span className='logo-emblem' />
					</div>
				</div>
			</div>

			<div className='container-narrow pb-xl'>
				<SignupDevNav />

				<h5 className='mb-xs text-center'>Sign up to UK Postbox</h5>

				<h3 className='mb-xs text-center'>Plan Configuration</h3>
				<div className='row'>
					<div className='col-xs-12'>
						<div className='row registration-wrapper'>
							<div className='col-xs-12 col-md-3'>
								<Card
									header={{
										title: 'Address Type',
										subTitle: 'Choose a plan type...',
									}}
									type={CardLayoutType.Default}
									emptyBody
								>
									<div className='m-sm'>
										{PlansOverview.map((addressPlan: SignUpPlanType, index: number) => {
											if (!addressPlan.enabled) return null;

											return (
												<ListItem
													title={addressPlan.label}
													description={addressPlan.description}
													hideArrow
													onClick={() => UpdatePlan(plan.complete, addressPlan)}
													hideSeparator
													extraClass={`list-item-selectable ${plan.id === addressPlan.id ? 'list-item-selected' : ''}`}
												/>
											);
										})}
									</div>
								</Card>
							</div>

							<div className={`col-xs-12 col-md-6 ${plan.id === 0 ? '' : 'hide'}`}>
								<Card
									header={{
										title: 'PO Box Location',
										subTitle: 'Choose a location...',
									}}
									type={CardLayoutType.Default}
									emptyBody
								>
									<div className='row no-gap'>
										<div className='col-xs-6'>
											<div className='m-sm'>
												{PoBoxOverview.map((poBoxPlan: SignUpPlanType, index: number) => {
													if (!poBoxPlan.enabled) return null;

													return (
														<ListItem
															title={poBoxPlan.label}
															description={poBoxPlan.labelExtra}
															hideArrow
															onLeave={() =>
																setPoBoxAddressDetails(
																	poBoxAddress.complete ? poBoxAddress : initialStep,
																)
															}
															onHover={() => setPoBoxAddressDetails(poBoxPlan)}
															onClick={() =>
																UpdatePoBoxAddress(poBoxAddress.complete, poBoxPlan)
															}
															hideSeparator
															extraClass={`list-item-selectable ${poBoxAddress.id === poBoxPlan.id ? 'list-item-selected' : ''}`}
														/>
													);
												})}
											</div>
										</div>

										<div className='col-xs-6'>
											<div style={{ backgroundColor: 'var(--surface-bg-alt)', height: '100%' }}>
												<div className='flex-column flex-gap-sm align-start p-md'>
													{PlanPerks.map((perk: SignUpPerkType, index: number) => (
														<div className='flex-row flex-gap-sm justify-center align-center'>
															{PoBoxOverview[0].perks.includes(perk.id) ? (
																<div className='pricing-block-perk-icon pricing-block-perk-included text-center'>
																	<FontAwesomeIcon icon={faCheckCircle} />
																</div>
															) : (
																<div className='pricing-block-perk-icon text-center'>
																	<FontAwesomeIcon icon={faXmarkCircle} />
																</div>
															)}

															<div>{perk.label}</div>

															{PoBoxOverview[0].tips &&
																PoBoxOverview[0].tips.includes(perk.id) && (
																	<div className='pricing-block-perk-tip mr-n-xl'>
																		<Tooltip content='Available on selected addresses and locations' />
																	</div>
																)}
														</div>
													))}
												</div>
											</div>
										</div>
									</div>
								</Card>
							</div>

							<div className={`col-xs-12 col-md-6 ${plan.id === 1 ? '' : 'hide'}`}>
								<Card
									header={{
										title: 'Street Location',
										subTitle: 'Choose a location...',
									}}
									type={CardLayoutType.Default}
									emptyBody
								>
									<div className='row no-gap'>
										<div className='col-xs-6'>
											<div className='m-sm'>
												{StreetAddressesOverview.map(
													(streetPlan: SignUpPlanType, index: number) => {
														if (!streetPlan.enabled) return null;

														return (
															<ListItem
																title={streetPlan.label}
																description={streetPlan.labelExtra}
																hideArrow
																onLeave={() =>
																	setStreetAddressDetails(
																		streetAddress.complete
																			? streetAddress
																			: initialStep,
																	)
																}
																onHover={() => setStreetAddressDetails(streetPlan)}
																onClick={() =>
																	UpdateStreetAddress(
																		streetAddress.complete,
																		streetPlan,
																	)
																}
																hideSeparator
																extraClass={`list-item-selectable ${streetAddress.id === streetPlan.id ? 'list-item-selected' : ''}`}
															/>
														);
													},
												)}
											</div>
										</div>

										<div className='col-xs-6'>
											<div style={{ backgroundColor: 'var(--surface-bg-alt)', height: '100%' }}>
												<div className='flex-column flex-gap-sm align-start p-md'>
													{StreetAddressesOverview.map((addressPlan: SignUpPlanType) => {
														if (addressPlan.id !== streetAddressDetails.id) return null;

														return (
															<>
																{PlanPerks.map((perk: SignUpPerkType) => (
																	<div className='flex-row flex-gap-sm justify-center align-center'>
																		{addressPlan.perks.includes(perk.id) ? (
																			<div className='pricing-block-perk-icon pricing-block-perk-included text-center'>
																				<FontAwesomeIcon icon={faCheckCircle} />
																			</div>
																		) : (
																			<div className='pricing-block-perk-icon text-center'>
																				<FontAwesomeIcon icon={faXmarkCircle} />
																			</div>
																		)}

																		<div>{perk.label}</div>

																		{addressPlan.tips &&
																			addressPlan.tips.includes(perk.id) && (
																				<div className='pricing-block-perk-tip mr-n-xl'>
																					<Tooltip content='Available on selected addresses and locations' />
																				</div>
																			)}
																	</div>
																))}
															</>
														);
													})}

													{!streetAddressDetails.id && (
														<>
															{PlanPerks.map((perk: SignUpPerkType) => (
																<div className='flex-row flex-gap-sm justify-center align-center'>
																	<div className='pricing-block-perk-icon pricing-block-perk-info text-center'>
																		<FontAwesomeIcon icon={faInfoCircle} />
																	</div>

																	<div>{perk.label}</div>
																</div>
															))}
														</>
													)}
												</div>
											</div>
										</div>
									</div>
								</Card>
							</div>

							<div className={`col-xs-12 col-md-6 ${plan.id === 2 ? '' : 'hide'}`}>
								<Card
									header={{ title: 'Business Location', subTitle: 'Choose a location...' }}
									type={CardLayoutType.Default}
									emptyBody
								>
									<div className='row no-gap'>
										<div className='col-xs-6'>
											<div className='m-sm'>
												{BusinessAddressesOverview.map(
													(businessPlan: SignUpPlanType, index: number) => {
														if (!businessPlan.enabled) return null;

														return (
															<ListItem
																title={businessPlan.label}
																description={businessPlan.labelExtra}
																hideArrow
																onLeave={() =>
																	setBusinessAddressDetails(
																		businessAddress.complete
																			? businessAddress
																			: initialStep,
																	)
																}
																onHover={() => setBusinessAddressDetails(businessPlan)}
																onClick={() =>
																	UpdateBusinessAddress(
																		businessAddress.complete,
																		businessPlan,
																	)
																}
																hideSeparator
																extraClass={`list-item-selectable ${businessAddress.id === businessPlan.id ? 'list-item-selected' : ''}`}
															/>
														);
													},
												)}
											</div>
										</div>

										<div className='col-xs-6'>
											<div style={{ backgroundColor: 'var(--surface-bg-alt)', height: '100%' }}>
												<div className='flex-column flex-gap-sm align-start p-md'>
													{BusinessAddressesOverview.map((addressPlan: SignUpPlanType) => {
														if (addressPlan.id !== businessAddressDetails.id) return null;

														return (
															<>
																{PlanPerks.map((perk: SignUpPerkType) => (
																	<div className='flex-row flex-gap-sm justify-center align-center'>
																		{addressPlan.perks.includes(perk.id) ? (
																			<div className='pricing-block-perk-icon pricing-block-perk-included text-center'>
																				<FontAwesomeIcon icon={faCheckCircle} />
																			</div>
																		) : (
																			<div className='pricing-block-perk-icon text-center'>
																				<FontAwesomeIcon icon={faXmarkCircle} />
																			</div>
																		)}

																		<div>{perk.label}</div>

																		{addressPlan.tips &&
																			addressPlan.tips.includes(perk.id) && (
																				<div className='pricing-block-perk-tip mr-n-xl'>
																					<Tooltip content='Available on selected addresses and locations' />
																				</div>
																			)}
																	</div>
																))}
															</>
														);
													})}

													{!businessAddressDetails.id && (
														<>
															{PlanPerks.map((perk: SignUpPerkType, index: number) => (
																<div className='flex-row flex-gap-sm justify-center align-center'>
																	<div className='pricing-block-perk-icon pricing-block-perk-info text-center'>
																		<FontAwesomeIcon icon={faInfoCircle} />
																	</div>

																	<div>{perk.label}</div>
																</div>
															))}
														</>
													)}
												</div>
											</div>
										</div>
									</div>
								</Card>
							</div>

							<div
								className={`col-xs-12 col-md-3 ${plan.id === 3 || plan.id === 4 || (plan.id === 0 && poBoxAddress.id) || (plan.id === 1 && streetAddress.id) || (plan.id === 2 && businessAddress.id) ? '' : 'hide'}`}
							>
								<Card
									header={{ title: 'Finalise', subTitle: 'Last bits of stuff...' }}
									type={CardLayoutType.Default}
								>
									{plan.id !== 3 && (
										<>
											<StackHorizontal align={FlexAlign.Center} extraClass='mb-xs'>
												<span className='list-item-title'>Letter Allowance</span>
												<div className='flex-grow ml-a'>
													<Tag
														hideIcon
														name={letterOption.label.split(' ')[1]}
														id={0}
														colour={Colour.Secondary}
													/>
												</div>
											</StackHorizontal>

											<input
												type='range'
												min={0}
												max={LettersOverview.length - 1}
												step={1}
												defaultValue={letterOption.id}
												onChange={(e) =>
													UpdateLetterOption(letterOption.complete, e.currentTarget.value)
												}
											/>

											<div style={{ height: '6em' }}>
												<StackVertical
													gap={FlexGap.ExtraSmall}
													align={FlexAlign.Start}
													extraClass='mt-sm mb-md'
												>
													{LettersOverview.map((letterPlan: SignUpPlanType) => {
														if (letterPlan.id !== letterOption.id) return null;

														return (
															<>
																{LetterPerks.map((perk: SignUpPerkType) => {
																	if (!letterPlan.perks.includes(perk.id))
																		return null;
																	return (
																		<div className='flex-row flex-gap-sm justify-center align-center'>
																			<div className='pricing-block-perk-icon pricing-block-perk-info text-center'>
																				<FontAwesomeIcon icon={faInfoCircle} />
																			</div>

																			<div>{perk.label}</div>

																			{letterPlan.tips &&
																				letterPlan.tips.includes(perk.id) && (
																					<div className='pricing-block-perk-tip mr-n-xl'>
																						<Tooltip content='Available on selected addresses and locations' />
																					</div>
																				)}
																		</div>
																	);
																})}
															</>
														);
													})}
												</StackVertical>
											</div>
										</>
									)}

									{plan.id === 3 && (
										<>
											<StackHorizontal align={FlexAlign.Center} extraClass='mb-xs'>
												<span className='list-item-title'>Parcel Allowance</span>
												<div className='flex-grow ml-a'>
													<Tag
														hideIcon
														name={parcelOption.label.split(' ')[1]}
														id={0}
														colour={Colour.Secondary}
													/>
												</div>
											</StackHorizontal>

											<input
												type='range'
												min={0}
												max={ParcelsOverview.length - 1}
												step={1}
												defaultValue={parcelOption.id}
												onChange={(e) =>
													UpdateParcelOption(parcelOption.complete, e.currentTarget.value)
												}
											/>

											<StackVertical
												gap={FlexGap.ExtraSmall}
												align={FlexAlign.Start}
												extraClass='mt-sm mb-md'
											>
												{ParcelsOverview.map((parcelPlan: SignUpPlanType) => {
													if (parcelPlan.id !== parcelOption.id) return null;

													return (
														<>
															{ParcelPerks.map((perk: SignUpPerkType) => {
																if (!parcelPlan.perks.includes(perk.id)) return null;
																return (
																	<div className='flex-row flex-gap-sm justify-center align-center'>
																		<div className='pricing-block-perk-icon pricing-block-perk-info text-center'>
																			<FontAwesomeIcon icon={faInfoCircle} />
																		</div>

																		<div>{perk.label}</div>

																		{parcelPlan.tips &&
																			parcelPlan.tips.includes(perk.id) && (
																				<div className='pricing-block-perk-tip mr-n-xl'>
																					<Tooltip content='Available on selected addresses and locations' />
																				</div>
																			)}
																	</div>
																);
															})}
														</>
													);
												})}
											</StackVertical>
										</>
									)}

									<StackVertical gap={FlexGap.ExtraSmall}>
										{plan.id === 0 && poBoxAddress.id && (
											<StackHorizontal justify={FlexJustify.Between} align={FlexAlign.Center}>
												<span className='list-item-title'>{poBoxAddress?.label} PO Box</span>

												<Pill
													content={FormatCurrency(poBoxAddress?.cost, true)}
													colour={Colour.Warning}
												/>
											</StackHorizontal>
										)}

										<StackHorizontal
											justify={FlexJustify.Between}
											align={FlexAlign.Center}
											extraClass={plan.id !== 2 || !businessAddress?.cost ? 'hide' : undefined}
										>
											<span className='list-item-title'>{businessAddress?.labelExtra}</span>

											<Pill
												content={FormatCurrency(businessAddress?.cost, true)}
												colour={Colour.Warning}
											/>
										</StackHorizontal>

										<StackHorizontal
											justify={FlexJustify.Between}
											align={FlexAlign.Center}
											extraClass={plan.id !== 1 || !streetAddress?.cost ? 'hide' : undefined}
										>
											<span className='list-item-title'>{streetAddress?.labelExtra}</span>

											<Pill
												content={FormatCurrency(streetAddress?.cost, true)}
												colour={Colour.Warning}
											/>
										</StackHorizontal>

										{plan.id !== 3 && (
											<StackHorizontal justify={FlexJustify.Between} align={FlexAlign.Center}>
												<span className='list-item-title'>{letterOption?.label}</span>

												<Pill
													content={FormatCurrency(letterOption?.cost, true)}
													colour={Colour.Warning}
												/>
											</StackHorizontal>
										)}

										{plan.id === 3 && (
											<StackHorizontal justify={FlexJustify.Between} align={FlexAlign.Center}>
												<span className='list-item-title'>{parcelOption?.label}</span>

												<Pill
													content={FormatCurrency(parcelOption?.cost, true)}
													colour={Colour.Warning}
												/>
											</StackHorizontal>
										)}

										<StackHorizontal justify={FlexJustify.Between} align={FlexAlign.Center}>
											<ToggleSwitch
												name='payAnnually'
												label='Pay Annually'
												defaultChecked={annual}
												onChange={(e: any) => setAnnual(e.currentTarget.checked)}
												type={ToggleType.Switch}
											/>
										</StackHorizontal>
									</StackVertical>

									<div className='pricing-block-price-info text-center'>
										{annual && <Pill content='Two Months Free' colour={Colour.Success} />}
										{annual && (
											<AnnualTotal
												planId={plan.id}
												letterOptionCost={letterOption?.cost as any}
												parcelOptionCost={parcelOption?.cost as any}
												businessAddressCost={businessAddress?.cost as any}
												poBoxAddressCost={poBoxAddress?.cost as any}
												streetAddressCost={streetAddress?.cost as any}
											/>
										)}

										{!annual && (
											<MonthlyTotal
												planId={plan.id}
												letterOptionCost={letterOption?.cost as any}
												parcelOptionCost={parcelOption?.cost as any}
												businessAddressCost={businessAddress?.cost as any}
												poBoxAddressCost={poBoxAddress?.cost as any}
												streetAddressCost={streetAddress?.cost as any}
											/>
										)}
										<small className='fade'>per month, inc VAT*</small>
									</div>

									<Button
										title='Continue'
										fullWidth
										colour={ColourStateSolid.Primary}
										onClick={() => UpdateAccountCreation()}
										disabled={
											(plan.id === 0 && !poBoxAddress.id) ||
											(plan.id === 2 && !businessAddress?.cost) ||
											(plan.id === 1 && !streetAddress?.cost)
										}
									/>
								</Card>
							</div>
						</div>

						<AccountCreation
							label='User Details'
							completeLabel='Complete'
							complete={accountCreation.complete}
							visible={accountCreation.visible}
							onCancelCallback={() => null}
							onChangeCallback={(e: any) => UpdateAccountCreation()}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

export default SignUpThree;
